import { useMemo, useState } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { stateStyle, STATE_META, displayName, StateIcon } from './tableStates';

const ROW_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Agrupa en filas por la posición vertical del plano (o de a 15 si no hay posiciones)
const buildRows = (units) => {
  const sorted = [...units].sort((a, b) => (a.position_y ?? 0) - (b.position_y ?? 0) || (a.position_x ?? 0) - (b.position_x ?? 0));
  const hasPos = new Set(units.map(u => Math.round((u.position_y ?? 0) / 8))).size > 1;
  const rows = [];
  if (hasPos) {
    sorted.forEach(u => {
      const last = rows[rows.length - 1];
      if (last && Math.abs((u.position_y ?? 0) - last.y) < 6) last.units.push(u);
      else rows.push({ y: u.position_y ?? 0, units: [u] });
    });
    rows.forEach(r => r.units.sort((a, b) => (a.position_x ?? 0) - (b.position_x ?? 0)));
  } else {
    const byName = [...units].sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true }));
    for (let i = 0; i < byName.length; i += 15) rows.push({ units: byName.slice(i, i + 15) });
  }
  return rows;
};

export const TableRowsGrid = ({ units, onSelect, onDoubleClick, selectedId }) => {
  const [zoom, setZoom] = useState(1);
  const rows = useMemo(() => buildRows(units), [units]);
  const size = Math.round(44 * zoom);
  return (
    <div className="bg-card border border-border rounded-2xl p-4" data-testid="table-rows-grid">
      <div className="flex justify-end gap-2 mb-3">
        <Button size="icon" variant="outline" data-testid="rows-zoom-out" onClick={() => setZoom(z => Math.max(0.6, +(z - 0.2).toFixed(1)))}><ZoomOut className="w-4 h-4" /></Button>
        <span className="text-xs self-center w-10 text-center" data-testid="rows-zoom-level">{Math.round(zoom * 100)}%</span>
        <Button size="icon" variant="outline" data-testid="rows-zoom-in" onClick={() => setZoom(z => Math.min(2.4, +(z + 0.2).toFixed(1)))}><ZoomIn className="w-4 h-4" /></Button>
      </div>
      <div className="overflow-auto space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`rows-row-${i}`}>
            <div className="w-14 shrink-0 text-xs font-bold text-muted-foreground">Fila {ROW_LETTERS[i] || i + 1}</div>
            {r.units.map(u => (
              <button key={u.unit_id} type="button" title={`${displayName(u)} · ${STATE_META[u.state]?.label}`}
                data-testid={`rows-cell-${u.unit_id}`} data-state={u.state} data-state-swatch
                onClick={() => onSelect?.(u)} onDoubleClick={() => onDoubleClick?.(u)}
                className={`rounded-lg font-bold grid place-items-center shrink-0 transition-transform hover:scale-110 ${STATE_META[u.state]?.blink ? 'table-blink' : ''} ${selectedId === u.unit_id ? 'ring-2 ring-offset-1 ring-foreground' : ''}`}
                style={{ ...stateStyle(u.state), width: size, height: size, fontSize: Math.max(9, 11 * zoom) }}>
                <StateIcon state={u.state} />{u.name.replace(/^\D+\s*/, '') || u.name}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
